import { NO_CASH_VALUE_DISCLOSURE, NO_CASH_VALUE_DISCLOSURE_TL } from '@tira/protocol';
import type { Phrase } from './copy.js';

/** Settings, the pitaka and the chip store — everything about the account. */
export const ACCOUNT = {
  /* ── Shared ───────────────────────────────────────────────────────────── */
  /*
   * The disclosure is not written out here. Design spec §13 ships it as one
   * string per language from the protocol so it cannot drift between the
   * store, the buy-in sheet and settings; the dictionary points at those.
   */
  'account.disclosure': { tl: NO_CASH_VALUE_DISCLOSURE_TL, en: NO_CASH_VALUE_DISCLOSURE },
  'account.chips': { tl: 'chips', en: 'chips' },
  'account.try': { tl: 'Subukan ulit', en: 'Try again' },

  /* ── Settings: responsible play ───────────────────────────────────────── */
  'account.settings.title': { tl: 'Settings', en: 'Settings' },
  'account.settings.sub': { tl: 'Responsableng laro muna', en: 'Responsible play first' },
  'account.care.title': { tl: 'Responsableng laro', en: 'Responsible play' },
  'account.care.body': {
    tl: 'Ikaw ang naglalagay ng hangganan. Susundin ’yan ng server, hindi mo na kailangang labanan.',
    en: 'You set the limits. The server enforces them, so you never have to fight them yourself.',
  },
  'account.care.excluded': {
    tl: 'Naka-self-exclusion ka — {until}. Hindi ka muna makakaupo sa kahit anong mesa.',
    en: 'You are self-excluded — {until}. You cannot sit at any table for now.',
  },
  'account.care.cooloff': {
    tl: 'Nasa cool-off ka — {until}. Balik ka pagkatapos.',
    en: 'You are on cool-off — {until}. Come back after.',
  },
  'account.until.hours': { tl: '{n} oras pa', en: '{n} hours left' },
  'account.until.days': { tl: '{n} araw pa', en: '{n} days left' },

  'account.reminder.label': { tl: 'Paalala sa haba ng session', en: 'Session length reminder' },
  'account.reminder.note': {
    tl: 'Tatapikin ka namin kapag umabot na sa oras na pinili mo.',
    en: 'We tap you when you hit the time you picked.',
  },
  'account.reminder.off': { tl: 'Wala', en: 'Off' },
  'account.reminder.min': { tl: '{n} min', en: '{n} min' },
  'account.reminder.saved.off': { tl: 'Naka-off na ang paalala.', en: 'Reminders are off.' },
  'account.reminder.saved': {
    tl: 'Paalala kada {n} minuto.',
    en: 'Reminder every {n} minutes.',
  },

  'account.cap.label': { tl: 'Sariling cap sa gastos kada araw', en: 'Your own daily spend cap' },
  'account.cap.note': {
    tl: 'Hinaharang ito sa checkout ng tindahan. Hindi ito tungkol sa chips — tungkol ito sa ginagastos mo sa app store. Ilagay ang 0 kung ayaw mo ng cap.',
    en: 'This stops you at store checkout. It is not about chips — it is about what you spend at the app store. Enter 0 for no cap.',
  },
  'account.cap.field': { tl: 'Halaga (PHP)', en: 'Amount (PHP)' },
  'account.cap.save': { tl: 'I-save ang cap', en: 'Save cap' },
  'account.cap.saved.off': { tl: 'Wala nang cap sa gastos.', en: 'No spend cap now.' },
  'account.cap.saved': { tl: 'Cap kada araw: PHP {v}.', en: 'Daily cap: PHP {v}.' },

  'account.dur.24h': { tl: '24 oras', en: '24 hours' },
  'account.dur.3d': { tl: '3 araw', en: '3 days' },
  'account.dur.7d': { tl: '7 araw', en: '7 days' },
  'account.dur.30d': { tl: '30 araw', en: '30 days' },
  'account.dur.90d': { tl: '90 araw', en: '90 days' },
  'account.dur.180d': { tl: '180 araw', en: '180 days' },

  'account.cooloff.label': { tl: 'Cool-off', en: 'Cool-off' },
  'account.cooloff.note': {
    tl: 'Pahinga muna sa mesa. Mananatili ang chips mo, pero hindi ka makakaupo hanggang matapos.',
    en: 'A break from the table. Your chips stay, but you cannot sit until it ends.',
  },
  'account.cooloff.title': { tl: 'Cool-off ng {dur}', en: 'Cool-off for {dur}' },
  'account.cooloff.body': {
    tl: 'Hindi ka makakaupo sa kahit anong mesa sa loob ng {dur}. Mababasa mo pa rin ang pitaka at ang compound.',
    en: 'You cannot sit at any table for {dur}. You can still read your wallet and the compound.',
  },
  'account.cooloff.hard': {
    tl: 'Hindi ito mababawi bago matapos — kahit i-uninstall at i-install mo ulit ang app, tuloy pa rin ang cool-off.',
    en: 'This cannot be lifted before it ends — uninstalling and reinstalling the app does not stop the cool-off.',
  },
  'account.cooloff.cta': { tl: 'Sige, {dur}', en: 'Go ahead, {dur}' },
  'account.cooloff.done': {
    tl: 'Nasa cool-off ka ng {dur}. Ingat.',
    en: 'You are on cool-off for {dur}. Take care.',
  },

  'account.exclude.label': { tl: 'Self-exclusion', en: 'Self-exclusion' },
  'account.exclude.note': {
    tl: 'Para sa mas mahabang pahinga. Isasara ang lahat ng mesa sa’yo sa panahong pinili mo.',
    en: 'For a longer break. Every table closes to you for the period you pick.',
  },
  'account.exclude.title': { tl: 'Self-exclusion ng {dur}', en: 'Self-exclusion for {dur}' },
  'account.exclude.body': {
    tl: 'Sa loob ng {dur}, hindi ka makakaupo, makakabili ng chips, o makakasali sa Kubo.',
    en: 'For {dur} you cannot sit, buy chips, or join a private room.',
  },
  'account.exclude.hard': {
    tl: 'Wala kaming paraan para bawiin ito nang maaga — hindi rin kayang paikliin ng support. Sigurado ka?',
    en: 'We have no way to lift this early — support cannot shorten it either. Are you sure?',
  },
  'account.exclude.cta': { tl: 'Oo, i-lock ako ng {dur}', en: 'Yes, lock me for {dur}' },
  'account.exclude.done': {
    tl: 'Naka-self-exclusion ka ng {dur}.',
    en: 'You are self-excluded for {dur}.',
  },

  'account.mute.label': { tl: 'I-mute ang table talk', en: 'Mute table talk' },
  'account.mute.note': {
    tl: 'Hindi mo na makikita ang mga preset ng kalaro mo. Preset lang naman ang lahat.',
    en: 'You stop seeing the presets your players send. They are all presets anyway.',
  },
  'account.mute.on': { tl: 'Naka-mute na ang table talk.', en: 'Table talk is muted.' },
  'account.mute.off': { tl: 'Bukas ulit ang table talk.', en: 'Table talk is back on.' },

  /* ── Settings: look and language ──────────────────────────────────────── */
  'account.look.title': { tl: 'Itsura at wika', en: 'Look and language' },
  'account.look.sub': {
    tl: 'Sa telepono mong ’to lang ito nakatago.',
    en: 'Kept on this phone only.',
  },
  'account.lang.label': { tl: 'Wika', en: 'Language' },
  'account.lang.note': {
    tl: 'Taglish ang default. Mas mahaba ang English, kasya pa rin.',
    en: 'Taglish is the default. English runs longer but still fits.',
  },
  'account.view.label': { tl: 'Anggulo ng mesa', en: 'Table angle' },
  'account.view.top': { tl: 'Tanaw', en: 'Top' },
  'account.view.note': {
    tl: 'Pareho ang laro sa dalawa. Mas malinaw ang tanaw sa maliit na screen; mas totoo ang 2.5D. Mapapalitan mo rin ’to habang naglalaro.',
    en: 'The game plays the same in both. Top is clearer on a small screen; 2.5D feels more real. You can switch while playing too.',
  },
  'account.sound.label': { tl: 'Tunog ng mesa', en: 'Table sounds' },
  'account.sound.off': { tl: 'Patay', en: 'Off' },
  'account.sound.mahina': { tl: 'Mahina', en: 'Soft' },
  'account.sound.katamtaman': { tl: 'Katamtaman', en: 'Normal' },
  'account.sound.malakas': { tl: 'Malakas', en: 'Loud' },
  'account.sound.note': {
    tl: 'Tansan, baraha, tile — maririnig mo ang bawat tira. Pindutin para marinig.',
    en: 'Caps, cards, tiles — every move lands with its own sound. Tap a level to hear it.',
  },
  'account.motion.label': { tl: 'Bawasan ang galaw', en: 'Reduce motion' },
  'account.motion.note': {
    tl: 'Papalitan ng simpleng fade ang mga physics: chip riffle, cap flip, camera push.',
    en: 'A plain fade replaces the physics: chip riffle, cap flip, camera push.',
  },
  'account.cb.label': { tl: 'Colourblind-safe', en: 'Colourblind-safe' },
  'account.cb.note': {
    tl: 'Dagdag na hugis at tatak sa piyesa at suit — hindi lang kulay ang pagkakaiba.',
    en: 'Extra shapes and marks on pieces and suits — colour is not the only cue.',
  },

  /* ── Settings: account ────────────────────────────────────────────────── */
  'account.acct.title': { tl: 'Account', en: 'Account' },
  'account.acct.guest': { tl: 'Bisita', en: 'Guest' },
  'account.acct.noemail': { tl: 'walang email', en: 'no email' },
  'account.acct.note': {
    tl: 'Minimal lang ang hawak naming impormasyon: handle, at email kung ni-link mo. Walang contacts, walang location.',
    en: 'We hold the bare minimum: your handle, and your email if you linked one. No contacts, no location.',
  },
  'account.delete.cta': { tl: 'Burahin ang account', en: 'Delete account' },
  'account.delete.body': {
    tl: 'Buburahin ang account mo, ang handle, at ang natitirang chips. Hindi mo na mababalikan ang kasaysayan ng laro mo.',
    en: 'This deletes your account, your handle, and any chips left. You cannot get your game history back.',
  },
  'account.delete.hard': {
    tl: 'Permanente ito. Walang cash value ang chips kaya walang maibabalik na kahit ano — wala ring paraan para ilipat ang mga ’yan kahit kanino.',
    en: 'This is permanent. Chips have no cash value, so there is nothing to give back — and no way to transfer them to anyone.',
  },
  'account.delete.yes': { tl: 'Oo, burahin na', en: 'Yes, delete it' },
  'account.confirm.no': { tl: 'Hindi muna', en: 'Not now' },
  'account.save.failed': {
    tl: 'Hindi na-save ang setting: {err}. Subukan ulit.',
    en: 'Setting not saved: {err}. Try again.',
  },
  'account.action.failed': {
    tl: 'Hindi natuloy: {err}. Subukan ulit.',
    en: 'That did not go through: {err}. Try again.',
  },

  /* ── Wallet ───────────────────────────────────────────────────────────── */
  'account.wallet.title': { tl: 'Pitaka', en: 'Wallet' },
  'account.wallet.sub': {
    tl: 'Saan galing at saan napunta ang chips mo',
    en: 'Where your chips came from and went',
  },
  'account.wallet.held': { tl: 'chips na hawak mo', en: 'chips on hand' },
  'account.wallet.escrow': { tl: 'Naka-hold sa mesa:', en: 'Held at the table:' },
  'account.wallet.escrow.back': {
    tl: 'Babalik ’yan sa pitaka pagkatapos ng laro.',
    en: 'It comes back to your wallet after the game.',
  },
  'account.wallet.baon.done': { tl: 'Nakuha na ang baon ngayon', en: 'Daily chips claimed' },
  'account.wallet.baon.claim': {
    tl: 'Kunin ang baon ngayong araw',
    en: 'Claim today’s chips',
  },
  'account.wallet.baon.got': {
    tl: 'Nakuha mo ang baon: {n} chips. Streak: {days} araw.',
    en: 'Daily chips in: {n} chips. Streak: {days} days.',
  },
  'account.wallet.baon.fail': {
    tl: 'Hindi makuha ang baon: {err}. Balik ka bukas.',
    en: 'Could not claim your daily chips: {err}. Come back tomorrow.',
  },
  'account.wallet.rescue.claim': { tl: 'Kunin ang rescue ({n})', en: 'Claim rescue ({n})' },
  'account.wallet.rescue.note': {
    tl: 'Hanggang {cap}x kada araw kapag wala pang {n} ang hawak mo.',
    en: 'Up to {cap}x a day when you hold under {n}.',
  },
  'account.wallet.rescue.locked': {
    tl: 'Rescue lang kapag wala nang {n} ang natitira sa’yo.',
    en: 'Rescue only when you have under {n} left.',
  },
  'account.wallet.rescue.got': {
    tl: 'Rescue chips: {n}. Dahan-dahan lang.',
    en: 'Rescue chips: {n}. Easy does it.',
  },
  'account.wallet.rescue.fail': {
    tl: 'Hindi makuha ang rescue: {err}. Subukan mamaya.',
    en: 'Could not claim rescue: {err}. Try later.',
  },
  'account.wallet.streak.title': { tl: 'Streak ng baon', en: 'Daily chip streak' },
  'account.wallet.streak.sub': {
    tl: 'Pitong araw na sunod-sunod, papataas ang baon.',
    en: 'Seven days in a row and the daily chips climb.',
  },
  'account.wallet.day': { tl: 'Araw {n}', en: 'Day {n}' },
  'account.wallet.streak.done': {
    tl: 'Kinuha mo na ang baon ngayon. Bukas: {n} chips.',
    en: 'Claimed for today. Tomorrow: {n} chips.',
  },
  'account.wallet.streak.waiting': {
    tl: 'Hintay ka pa ng {n} chips ngayong araw. Kapag nalaktawan mo, babalik sa Araw 1.',
    en: '{n} chips are waiting for you today. Miss a day and you drop back to Day 1.',
  },
  'account.wallet.stats.title': { tl: 'Panalo kada laro', en: 'Wins by game' },
  'account.wallet.stats.sub': {
    tl: 'Buong kasaysayan mo, hindi season lang.',
    en: 'Your whole history, not just this season.',
  },
  'account.wallet.stats.none': { tl: 'Wala pang laro dito', en: 'No games here yet' },
  'account.wallet.stats.record': {
    tl: '{won}W · {lost}L sa {played}',
    en: '{won}W · {lost}L of {played}',
  },
  'account.wallet.ledger.title': { tl: 'Galaw ng chips', en: 'Chip movement' },
  'account.wallet.ledger.sub': {
    tl: 'Bawat linya, isang galaw. Kasama ang tong.',
    en: 'One line per move. Rake included.',
  },
  'account.wallet.ledger.failed': {
    tl: 'Hindi ma-load ang kasaysayan.',
    en: 'Could not load your history.',
  },
  'account.wallet.ledger.failed.sub': {
    tl: 'Suriin ang koneksyon mo, tapos i-tap ang “Subukan ulit”.',
    en: 'Check your connection, then tap “Try again”.',
  },
  'account.wallet.ledger.loading': { tl: 'Kinukuha ang listahan…', en: 'Loading the list…' },
  'account.wallet.ledger.empty': { tl: 'Wala pang galaw dito.', en: 'Nothing has moved yet.' },
  'account.wallet.ledger.empty.sub': {
    tl: 'Kunin ang baon o umupo sa isang mesa — dito lalabas ang bawat chip.',
    en: 'Claim your daily chips or sit at a table — every chip shows up here.',
  },
  'account.wallet.ledger.back': { tl: 'Balik sa compound', en: 'Back to the compound' },
  'account.wallet.more': { tl: 'Marami pa', en: 'More' },
  'account.wallet.page.fail': {
    tl: 'Hindi na-load ang susunod na pahina. Subukan ulit.',
    en: 'Could not load the next page. Try again.',
  },

  /* Ledger kinds, in the words a player would use. */
  'account.kind.starter_rack': { tl: 'Starter rack', en: 'Starter rack' },
  'account.kind.daily_baon': { tl: 'Baon ng araw', en: 'Daily chips' },
  'account.kind.hourly_rescue': { tl: 'Rescue chips', en: 'Rescue chips' },
  'account.kind.iap_topup': { tl: 'Top up sa tindahan', en: 'Store top-up' },
  'account.kind.buyin_escrow': { tl: 'Umupo sa mesa (buy-in)', en: 'Sat at a table (buy-in)' },
  'account.kind.buyin_refund': { tl: 'Balik ng buy-in', en: 'Buy-in returned' },
  'account.kind.pot_payout': { tl: 'Hatian ng pot', en: 'Pot split' },
  'account.kind.tong': { tl: 'Tong ng bahay', en: 'House rake' },
  'account.kind.admin_adjust': { tl: 'Ayos ng support', en: 'Support adjustment' },

  /* ── Store ────────────────────────────────────────────────────────────── */
  'account.store.title': { tl: 'Tindahan', en: 'Store' },
  'account.store.sub': { tl: 'Chips lang ang binibili dito', en: 'Chips are all you buy here' },
  'account.store.rack': {
    tl: 'Kasama na ang starter rack mo',
    en: 'Your starter rack is included',
  },
  'account.store.rack.note': {
    tl: '{n} chips ang binuksan sa unang login — bayad na ’yan sa app, hindi na uulitin.',
    en: '{n} chips opened on your first login — that comes with the app, once only.',
  },
  'account.store.sandbox.flag': { tl: 'Sandbox na bayad', en: 'Sandbox payments' },
  'account.store.packs': { tl: 'Mga pack ng chips', en: 'Chip packs' },
  'account.store.packs.sub': {
    tl: 'Isang tap, dagdag chips. Walang subscription.',
    en: 'One tap, more chips. No subscription.',
  },
  'account.store.best': { tl: 'Pinaka-sulit', en: 'Best value' },
  'account.store.price': { tl: 'Bayad sa store: PHP {php}', en: 'Store price: PHP {php}' },
  'account.store.price.note': {
    tl: 'Ang huling halaga at currency ay galing sa app store mo.',
    en: 'Your app store sets the final price and currency.',
  },
  'account.store.busy': { tl: 'Sandali…', en: 'One sec…' },
  'account.store.buy': { tl: 'Kunin', en: 'Get' },
  'account.store.sandbox.note': {
    tl: 'Sandbox pa ang bayaran dito: walang totoong sisingilin, at ang server pa rin ang nagdaragdag ng chips — hindi ang app sa telepono mo.',
    en: 'Payments here are still sandbox: nothing is really charged, and the server still credits the chips — not the app on your phone.',
  },
  'account.store.bought': {
    tl: 'Nadagdag ang {n} chips sa pitaka mo.',
    en: '{n} chips added to your wallet.',
  },
  'account.store.fail': {
    tl: 'Hindi natuloy ang top up: {err}. Subukan ulit mamaya.',
    en: 'Top-up did not go through: {err}. Try again later.',
  },
  'account.store.capped': {
    tl: 'Naabot mo na ang sarili mong cap sa gastos ngayong araw.',
    en: 'You have hit your own spend cap for today.',
  },
  'account.store.capped.sub': {
    tl: 'Hindi natuloy ang top up. Bukas ulit, o baguhin mo ang cap sa Settings kung mali ang nailagay mo.',
    en: 'The top-up did not go through. Try tomorrow, or change the cap in Settings if you typed it wrong.',
  },
  'account.store.settings': { tl: 'Buksan ang Settings', en: 'Open Settings' },
  'account.store.care.note': {
    tl: 'Cap sa gastos, paalala sa haba ng session, cool-off at self-exclusion.',
    en: 'Spend cap, session reminders, cool-off and self-exclusion.',
  },
  'account.store.open': { tl: 'Buksan', en: 'Open' },
  /* Singulars: English inflects after a count and Taglish does not. */
  'account.until.hours.one': { tl: '{n} oras pa', en: '{n} hour left' },
  'account.until.days.one': { tl: '{n} araw pa', en: '{n} day left' },
} as const satisfies Record<string, Phrase>;
